import { defineStore } from 'pinia';
import { sb } from '../composables/useSupabase.js';

export const useAirjumpStore = defineStore('airjump', {
  state: () => ({
    // globals (loaded once)
    taxonomyRaw: [],
    operators: [],
    globalsLoaded: false,

    // conversation-level
    phone: '',
    convId: null,
    clientId: null,
    productFamily: '',
    productSubtag: '',
    statusTag: '',
    statusSubtag: '',
    autopilotActive: false,
    aiSummary: '',
    quotations: [],
    priceLists: [],
    convLoading: false,
    qqList: [],
    qqLoaded: false,
    companyData: null,

    // Modal state (persists across sidebar close/reopen)
    showQuotation: false,
    showSendQuotation: false,
    showPriceList: false,
    showClientLink: false,
    showPortfolio: false,
    showBurbuja: false,
    showDelete: false,
    sendQuotationPreselect: null,
    deleteTarget: null,
  }),

  getters: {
    taxonomyByCategory(state) {
      const byCategory = {};
      state.taxonomyRaw.forEach(r => {
        if (!byCategory[r.category]) byCategory[r.category] = {};
        if (!byCategory[r.category][r.family]) {
          byCategory[r.category][r.family] = {
            family: r.family,
            family_display: r.family_display,
            subtags: [],
          };
        }
        if (r.subtag) {
          byCategory[r.category][r.family].subtags.push({
            subtag: r.subtag,
            subtag_display: r.subtag_display,
            is_default: r.is_default,
          });
        }
      });
      return byCategory;
    },

    products() {
      return this.taxonomyByCategory.product || {};
    },
    workflows() {
      return this.taxonomyByCategory.workflow || {};
    },
    statuses() {
      return this.taxonomyByCategory.status || {};
    },

    productFamilies() {
      return Object.values(this.products).map(p => ({
        family: p.family,
        family_display: p.family_display,
        subtags: p.subtags,
      }));
    },
    workflowFamilies() {
      return Object.values(this.workflows).map(w => ({
        family: w.family,
        family_display: w.family_display,
        subtags: w.subtags,
      }));
    },
    statusFamilies() {
      return Object.values(this.statuses).map(s => ({
        family: s.family,
        family_display: s.family_display,
        subtags: s.subtags,
      }));
    },
  },

  actions: {
    async loadGlobals() {
      if (this.globalsLoaded) return;

      const [taxonomy, operators] = await Promise.all([
        sb('wa_taxonomy?active=eq.true&order=category.asc,sort_order.asc'),
        sb('wa_operators?select=id,name,short_name&order=id.asc'),
      ]);

      this.taxonomyRaw = taxonomy || [];
      this.operators = operators || [];
      this.globalsLoaded = true;
    },

    async loadConversation(phone) {
      if (!phone) return;
      this.phone = phone;
      this.convLoading = true;

      try {
        // Fetch conversation record
        const convRows = await sb(
          `wa_conversations?phone=eq.${phone}&select=id,client_id,product_family,product_subtag,status_tag,status_subtag,ai_context`
        );
        const conv = convRows?.[0];
        if (conv) {
          this.convId = conv.id;
          this.clientId = conv.client_id;
          this.productFamily = conv.product_family || '';
          this.productSubtag = conv.product_subtag || '';
          this.statusTag = conv.status_tag || '';
          this.statusSubtag = conv.status_subtag || '';
          this.aiSummary = conv.ai_context?.summary || '';
        } else {
          this.convId = null;
          this.productFamily = '';
          this.productSubtag = '';
          this.statusTag = '';
          this.statusSubtag = '';
          this.aiSummary = '';
          // No wa_conversations record — try to find client directly by phone
          const clientRows = await sb(
            `clients?or=(phone.eq.${phone},normalized_phone.eq.${phone})&select=id&limit=1`
          );
          this.clientId = clientRows?.[0]?.id || null;
        }

        // Fetch autopilot status from session_vars
        const autopilotRows = await sb(
          `session_vars?phone=eq.${phone}&variable=eq.autopilot_active&select=value`
        );
        this.autopilotActive = autopilotRows?.[0]?.value === 'true';

        // Fetch documents (quotations + price lists)
        await this.refreshDocuments();
      } finally {
        this.convLoading = false;
      }
    },

    async refreshDocuments() {
      // Quotations — try by client_id first, fallback to conversation_id
      let quotes = null;
      if (this.clientId) {
        quotes = await sb(
          `quotations?client_id=eq.${this.clientId}&select=id,title,status,created_at&order=created_at.desc`
        );
      }
      if ((!quotes || !quotes.length) && this.convId) {
        quotes = await sb(
          `quotations?conversation_id=eq.${this.convId}&select=id,title,status,created_at&order=created_at.desc`
        );
      }
      if (this.clientId || this.convId) {
        if (quotes && quotes.length > 0) {
          // Fetch variables for interpolation
          const ids = quotes.map(q => q.id).join(',');
          const vars = await sb(
            `quotation_variables?quotation_id=in.(${ids})&select=quotation_id,name,value`
          );
          // Group variables by quotation_id
          const varMap = {};
          (vars || []).forEach(v => {
            if (!varMap[v.quotation_id]) varMap[v.quotation_id] = {};
            varMap[v.quotation_id][v.name] = v.value;
          });
          // Interpolate titles
          this.quotations = quotes.map(q => {
            let title = q.title || '';
            const qVars = varMap[q.id];
            if (qVars) {
              Object.entries(qVars).forEach(([name, value]) => {
                title = title.replace(`$${name}`, value);
              });
            }
            return { ...q, title };
          });
        } else {
          this.quotations = [];
        }
      } else {
        this.quotations = [];
      }

      // Price lists
      if (this.convId) {
        const lists = await sb(
          `price_lists?conversation_id=eq.${this.convId}&select=id,title,created_at&order=created_at.desc`
        );
        this.priceLists = lists || [];
      } else if (this.phone) {
        const lists = await sb(
          `price_lists?phone=eq.${this.phone}&select=id,title,created_at&order=created_at.desc`
        );
        this.priceLists = lists || [];
      } else {
        this.priceLists = [];
      }
    },

    async setProduct(family, subtag) {
      if (!this.convId) return;
      const body = { product_family: family, product_subtag: subtag };
      const result = await sb(`wa_conversations?id=eq.${this.convId}`, {
        method: 'PATCH',
        body,
        prefer: 'return=representation',
      });
      if (result) {
        this.productFamily = family;
        this.productSubtag = subtag;
      }
    },

    async setStatus(tag, subtag) {
      if (!this.convId) return;
      const body = { status_tag: tag, status_subtag: subtag };
      const result = await sb(`wa_conversations?id=eq.${this.convId}`, {
        method: 'PATCH',
        body,
        prefer: 'return=representation',
      });
      if (result) {
        this.statusTag = tag;
        this.statusSubtag = subtag;
      }
    },

    async activateAutopilot(mode, instruction) {
      if (!this.phone) return;
      const payload = {
        phone: this.phone,
        command: 'activate_autopilot',
        payload: { mode },
        status: 'pending',
      };
      if (mode === 'instruction' && instruction) {
        payload.payload.instruction = instruction;
      }
      const result = await sb('wa_commands', {
        method: 'POST',
        body: payload,
      });
      if (result) {
        this.autopilotActive = true;
      }
    },

    async deactivateAutopilot() {
      if (!this.phone) return;
      const result = await sb(
        `session_vars?phone=eq.${this.phone}&variable=eq.autopilot_active`,
        {
          method: 'POST',
          body: { phone: this.phone, variable: 'autopilot_active', value: 'false' },
          prefer: 'resolution=merge-duplicates,return=representation',
        }
      );
      if (result) {
        this.autopilotActive = false;
      }
    },

    async loadQQList() {
      if (this.qqLoaded) return;
      const { rpc } = await import('../composables/useRpc.js');
      const data = await rpc('qq-list');
      if (data) { this.qqList = data; this.qqLoaded = true; }
    },

    async loadCompanyData(_phone) {
      // No-op: company data comes from crm_init via loadConversation
    },

    async deleteQuotation(id) {
      const { rpc } = await import('../composables/useRpc.js');
      await rpc('delete-quotation', { body: { quotation_id: id } });
      this.quotations = this.quotations.filter(q => q.id !== id);
    },

    async deletePriceList(id) {
      const { rpc } = await import('../composables/useRpc.js');
      await rpc('delete-price-list', { body: { price_list_id: id } });
      this.priceLists = this.priceLists.filter(p => p.id !== id);
    },

    async sendPriceList(id) {
      const { rpc } = await import('../composables/useRpc.js');
      await rpc('send-price-list', { body: { price_list_id: id, phone: this.phone, conversation_id: this.convId } });
    },
  },
});
